import { create } from "zustand"

type ParserSelectionStore = {
  selectMode: boolean
  selectedFileIndices: Set<number>
  selectedColumnIds: Set<string>
  lastSelectedFileIndex: number | null
  toggleSelectMode: () => void
  enterSelectModeWithFile: (index: number) => void
  toggleFileSelection: (index: number) => void
  toggleColumnSelection: (columnId: string) => void
  selectFileRange: (startIndex: number, endIndex: number) => void
  selectAllFiles: (fileIndices: number[]) => void
  clearFileSelection: () => void
  clearColumnSelection: () => void
  clearAllSelection: () => void
  exitSelectMode: () => void
}

export const useParserSelectionStore = create<ParserSelectionStore>((set, get) => ({
  selectMode: false,
  selectedFileIndices: new Set(),
  selectedColumnIds: new Set(),
  lastSelectedFileIndex: null,

  toggleSelectMode: () => {
    const currentMode = get().selectMode
    if (currentMode) {
      set({
        selectMode: false,
        selectedFileIndices: new Set(),
        selectedColumnIds: new Set(),
        lastSelectedFileIndex: null,
      })
    } else {
      set({ selectMode: true })
    }
  },

  enterSelectModeWithFile: (index) => {
    const currentMode = get().selectMode
    if (currentMode) {
      get().toggleFileSelection(index)
      return
    }

    set({
      selectMode: true,
      selectedFileIndices: new Set([index]),
      selectedColumnIds: new Set(),
      lastSelectedFileIndex: index,
    })
  },

  toggleFileSelection: (index) => {
    const { selectedFileIndices } = get()
    const newSelection = new Set(selectedFileIndices)

    if (newSelection.has(index)) {
      newSelection.delete(index)
    } else {
      newSelection.add(index)
    }

    set({ selectedFileIndices: newSelection, lastSelectedFileIndex: index })
  },

  toggleColumnSelection: (columnId) => {
    const { selectedColumnIds } = get()
    const newSelection = new Set(selectedColumnIds)

    if (newSelection.has(columnId)) {
      newSelection.delete(columnId)
    } else {
      newSelection.add(columnId)
    }

    set({ selectedColumnIds: newSelection })
  },

  selectFileRange: (startIndex, endIndex) => {
    const minIndex = Math.min(startIndex, endIndex)
    const maxIndex = Math.max(startIndex, endIndex)
    const newSelection = new Set<number>()

    for (let index = minIndex; index <= maxIndex; index++) {
      newSelection.add(index)
    }

    set({ selectedFileIndices: newSelection, lastSelectedFileIndex: endIndex })
  },

  selectAllFiles: (fileIndices) => {
    const { selectedFileIndices } = get()
    const allSelected = fileIndices.every((index) => selectedFileIndices.has(index))

    if (allSelected) {
      set({ selectedFileIndices: new Set() })
    } else {
      set({ selectedFileIndices: new Set(fileIndices) })
    }
  },

  clearFileSelection: () => {
    set({ selectedFileIndices: new Set(), lastSelectedFileIndex: null })
  },

  clearColumnSelection: () => {
    set({ selectedColumnIds: new Set() })
  },

  clearAllSelection: () => {
    set({
      selectedFileIndices: new Set(),
      selectedColumnIds: new Set(),
      lastSelectedFileIndex: null,
    })
  },

  exitSelectMode: () => {
    set({
      selectMode: false,
      selectedFileIndices: new Set(),
      selectedColumnIds: new Set(),
      lastSelectedFileIndex: null,
    })
  },
}))
